import { Injectable, OnDestroy, effect, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { AppBadgeService, withUnreadPrefix } from '@core/services/infra/app-badge.service';
import { TranslationService } from './translation.service';

/**
 * Runs route titles through i18n, and re-applies them when the language changes.
 *
 * Angular's default strategy writes `route.title` to the document verbatim, so
 * every title in the app was a hardcoded string — mostly English, a few French
 * ("Avis de valeur | Proporio"), none of them following the user's language.
 *
 * Backwards compatible on purpose: ngx-translate returns the input unchanged
 * when it is not a known key, so routes still carrying a literal title keep
 * working and can be migrated to keys one at a time.
 *
 * The last key is remembered because a language change does not re-run the
 * router — without it the tab would keep the previous language until the next
 * navigation. It also covers the cold-start case where the first navigation
 * resolves before the translation files have loaded: the first language
 * emission re-applies the title.
 *
 * It also owns the unread-count prefix (`(3) Cockpit`). That belongs here, not
 * in a service of its own: the document title has exactly one writer, and a
 * second one would be overwritten by the next navigation. Three inputs, one
 * place that composes them — the route key, the language, and the count.
 */
@Injectable()
export class ProporioTitleStrategy extends TitleStrategy implements OnDestroy {
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly translation = inject(TranslationService);
  private readonly badge = inject(AppBadgeService);
  private readonly destroy$ = new Subject<void>();

  private currentKey?: string;

  /**
   * The title index.html shipped with, captured before anything overwrites it.
   *
   * It is the base for every route that declares no `title` — which is nearly
   * all of them: 15 routes in the whole app do, and they are mostly public
   * landings. Without this fallback the unread prefix would only ever show on
   * those 15, i.e. never on the screens a manager actually sits on.
   */
  private readonly baseTitle: string;

  constructor() {
    super();
    this.baseTitle = this.title.getTitle();
    this.translation.onLanguageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.apply());
    // The count changes without a navigation and without a language change, so
    // it needs its own re-apply — same reasoning as the language subscription.
    effect(() => {
      this.badge.unreadCount();
      this.apply();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.currentKey = this.buildTitle(snapshot);
    this.apply();
  }

  private apply(): void {
    // A route with no title keeps the document's own — unchanged from before —
    // but still gets the unread prefix. Only the prefix is new here.
    const base = this.currentKey === undefined ?
      this.baseTitle :
      this.translate.instant(this.currentKey);
    this.title.setTitle(withUnreadPrefix(base, this.badge.unreadCount()));
  }
}
